import { wrapIndex, swipeDirection, slideshowInterval } from './lf-utils.js';

/**
 * Hero slideshow. Markup contract: slides are `[data-lf-slide]` (the first carries `is-active`), indicators are
 * `[data-lf-indicator]` buttons in slide order, and `interval` is the autoplay delay in ms (default 6000).
 * CSS stacks every slide in one grid cell, so the slideshow is as tall as its tallest slide. Inactive slides are
 * `visibility: hidden` with their `img` set to `display: none`, so their lazy images stay unloaded; this element only
 * moves `is-active`, `aria-hidden` and `inert`.
 *
 * Autoplay is off for one slide and under prefers-reduced-motion. It pauses while a mouse hovers, while keyboard focus
 * is inside, during a touch, while the tab is hidden or the slideshow is off screen, and while a slide is selected in
 * the theme editor. A visible pause/play button ([data-lf-pause], shown only when the slideshow can play) lets anyone stop
 * it for good (WCAG 2.2.2). Touch and pen swipe to change slide; CSS keeps vertical panning native (touch-action: pan-y).
 */
class LfSlideshow extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement[]} */
  #indicators = [];
  /** @type {HTMLElement} */
  #viewport = this;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #index = 0;
  #timer = 0;
  #hovered = false;
  #focused = false;
  #touching = false;
  #editing = false;
  #userPaused = false;
  #visible = true;
  #startX = 0;
  #startY = 0;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-lf-slide]')]);
    this.#indicators = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-lf-indicator]')]);
    this.#viewport = /** @type {HTMLElement} */ (this.querySelector('[data-lf-viewport]') ?? this);
    this.#show(Math.max(0, this.#slides.findIndex((slide) => slide.classList.contains('is-active'))));
    this.setAttribute('data-ready', '');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerCancel);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.#motion.addEventListener('change', this.#sync);
    document.addEventListener('visibilitychange', this.#sync);

    if (typeof IntersectionObserver !== 'undefined') {
      this.#observer = new IntersectionObserver((entries) => {
        this.#visible = entries[entries.length - 1].isIntersecting;
        this.#sync();
      });
      this.#observer.observe(this);
    }
    this.#sync();
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#timer = 0;
    this.#observer?.disconnect();
    this.#observer = null;

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerCancel);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.#motion.removeEventListener('change', this.#sync);
    document.removeEventListener('visibilitychange', this.#sync);
  }

  /** @param {number} index */
  #show(index) {
    // CSS fades a slide in only after the slideshow has moved, so the first paint is never delayed by an animation.
    if (this.hasAttribute('data-ready') && index !== this.#index) this.setAttribute('data-moved', '');
    this.#index = index;
    this.#slides.forEach((slide, position) => {
      const active = position === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    this.#indicators.forEach((indicator, position) => indicator.setAttribute('aria-current', String(position === index)));
  }

  /** @param {number} delta */
  #step(delta) {
    this.#show(wrapIndex(this.#index, delta, this.#slides.length));
    this.#sync();
  }

  #sync = () => {
    window.clearInterval(this.#timer);
    this.#timer = 0;

    const canPlay = this.#slides.length > 1 && !this.#motion.matches;
    const paused = this.#userPaused || this.#hovered || this.#focused || this.#touching || this.#editing || document.hidden || !this.#visible;
    const playing = canPlay && !paused;

    const pause = this.querySelector('[data-lf-pause]');
    if (pause instanceof HTMLElement) {
      pause.hidden = !canPlay;
      pause.toggleAttribute('data-paused', this.#userPaused);
      pause.setAttribute('aria-label', this.#userPaused ? 'Play slideshow' : 'Pause slideshow');
    }

    // A rotating region stays quiet for screen readers; a stopped one announces the slide a user moves to.
    this.#viewport.setAttribute('aria-live', playing ? 'off' : 'polite');
    if (playing) this.#timer = window.setInterval(() => this.#step(1), slideshowInterval(this.getAttribute('interval')));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-lf-pause]')) {
      this.#userPaused = !this.#userPaused;
      if (!this.#userPaused) this.#focused = false;
      this.#sync();
      return;
    }
    const indicator = event.target instanceof Element ? event.target.closest('[data-lf-indicator]') : null;
    const position = indicator ? this.#indicators.indexOf(/** @type {HTMLElement} */ (indicator)) : -1;
    if (position === -1) return;
    this.#show(position);
    this.#sync();
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    this.#sync();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#touching = true;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#sync();
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#touching) return;
    this.#touching = false;
    const direction = swipeDirection(event.clientX - this.#startX, event.clientY - this.#startY);
    if (direction !== 0) this.#step(direction);
    else this.#sync();
  };

  #onPointerCancel = () => {
    this.#touching = false;
    this.#sync();
  };

  /**
   * Only keyboard focus pauses; a mouse click on an indicator must not leave the slideshow stuck.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    this.#focused = event.target instanceof Element && event.target.matches(':focus-visible');
    this.#sync();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#focused = false;
    this.#sync();
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const position = this.#slides.indexOf(/** @type {HTMLElement} */ (event.target));
    if (position === -1) return;
    this.#editing = true;
    this.#show(position);
    this.#sync();
  };

  #onBlockDeselect = () => {
    this.#editing = false;
    this.#sync();
  };
}

if (!customElements.get('lf-slideshow')) customElements.define('lf-slideshow', LfSlideshow);
